import type { PromotionOffer } from './api-game-promotions';

/**
 * The Offers form on a promotion (D-377, D-383), for adding an offer and for
 * editing one. Pure, so what the form sends is tested without a browser: the
 * same fields in both directions, and an edit that sends every field it
 * shows (a cleared limit arrives as `null`, which the API reads as "no limit",
 * rather than being left out and kept).
 */

export type OfferKind = PromotionOffer['offer_kind'];
export type DealType = 'PERCENT_OFF' | 'AMOUNT_OFF' | 'FIXED_PRICE' | 'BUY_X_GET_Y';

export interface OfferFormState {
  offer_kind: OfferKind;
  label: string;
  detail: string;
  giveaway_quantity: string;
  promotion_type: DealType;
  discount_scope: 'PER_TICKET' | 'PER_ORDER';
  /** Percent, dollars off, or the fixed price in dollars, by type. */
  amount: string;
  buy_quantity: string;
  get_quantity: string;
  get_percent: string;
  ticket_type_codes: string;
  price_zone_codes: string;
  web: boolean;
  box_office: boolean;
  min_quantity: string;
  max_quantity_per_order: string;
  usage_cap_total: string;
  /** Adding only: a deal starts active or as a draft. An edit uses Activate / Pause. */
  activate: boolean;
  item_name: string;
  item_description: string;
  price: string;
  max_per_order: string;
  stock_limit: string;
}

export const blankOffer: OfferFormState = {
  offer_kind: 'INFO',
  label: '',
  detail: '',
  giveaway_quantity: '',
  promotion_type: 'BUY_X_GET_Y',
  discount_scope: 'PER_TICKET',
  amount: '',
  buy_quantity: '1',
  get_quantity: '1',
  get_percent: '100',
  ticket_type_codes: '',
  price_zone_codes: '',
  web: true,
  box_office: true,
  min_quantity: '',
  max_quantity_per_order: '',
  usage_cap_total: '',
  activate: true,
  item_name: '',
  item_description: '',
  price: '',
  max_per_order: '10',
  stock_limit: '',
};

const DEAL_TYPES: readonly DealType[] = ['PERCENT_OFF', 'AMOUNT_OFF', 'FIXED_PRICE', 'BUY_X_GET_Y'];

/** "ADULT, senior  GA" → ["ADULT", "SENIOR", "GA"]. */
export const codes = (text: string): string[] => text.split(/[,\s]+/).map((value) => value.trim().toUpperCase()).filter(Boolean);

const cents = (dollars: string): number => Math.round(Number(dollars || 0) * 100);
const count = (text: string): number | null => (text.trim() === '' ? null : Number(text));
const text = (value: number | null | undefined): string => (value === null || value === undefined ? '' : String(value));
const dollarText = (value: number | null | undefined): string => (value === null || value === undefined ? '' : (value / 100).toFixed(2));

/** The form, filled from an offer that exists. */
export function offerFormFrom(offer: PromotionOffer): OfferFormState {
  const type = DEAL_TYPES.includes(offer.promotion_type as DealType) ? (offer.promotion_type as DealType) : 'BUY_X_GET_Y';
  // A deal with no channel restriction applies everywhere; a special names its channels.
  const channels = offer.offer_kind === 'CONCESSION_SPECIAL' ? (offer.product_channels ?? []) : (offer.restrictions?.channels ?? []);
  const everywhere = offer.offer_kind === 'TICKET_DEAL' && channels.length === 0;
  return {
    ...blankOffer,
    offer_kind: offer.offer_kind,
    label: offer.label ?? '',
    detail: offer.detail ?? '',
    giveaway_quantity: text(offer.giveaway_quantity),
    promotion_type: type,
    discount_scope: offer.discount_scope ?? 'PER_TICKET',
    amount:
      type === 'PERCENT_OFF' ? text(offer.discount_basis_points === null ? null : offer.discount_basis_points / 100) : type === 'AMOUNT_OFF' ? dollarText(offer.discount_amount_cents) : type === 'FIXED_PRICE' ? dollarText(offer.fixed_price_cents) : '',
    buy_quantity: text(offer.buy_quantity ?? 1),
    get_quantity: text(offer.get_quantity ?? 1),
    get_percent: text((offer.get_discount_basis_points ?? 10000) / 100),
    ticket_type_codes: (offer.restrictions?.ticket_type_codes ?? []).join(', '),
    price_zone_codes: (offer.restrictions?.price_zone_codes ?? []).join(', '),
    web: everywhere || channels.includes('WEB'),
    box_office: everywhere || channels.includes('BOX_OFFICE'),
    min_quantity: text(offer.min_quantity),
    max_quantity_per_order: text(offer.max_quantity_per_order),
    usage_cap_total: text(offer.usage_cap_total),
    activate: offer.deal_status === 'ACTIVE',
    item_name: offer.product_name ?? '',
    item_description: offer.product_description ?? '',
    price: dollarText(offer.product_price_cents),
    max_per_order: text(offer.product_max_per_order ?? 10),
    stock_limit: text(offer.product_stock_limit),
  };
}

/** The deal's terms as the API takes them, on create and on edit alike. */
function dealTerms(form: OfferFormState): Record<string, unknown> {
  const amount = Number(form.amount || 0);
  const channels = [...(form.web ? ['WEB'] : []), ...(form.box_office ? ['BOX_OFFICE'] : [])];
  return {
    promotion_type: form.promotion_type,
    // Only an amount off can come off the whole order (D-163).
    discount_scope: form.promotion_type === 'AMOUNT_OFF' ? form.discount_scope : 'PER_TICKET',
    ...(form.promotion_type === 'PERCENT_OFF' ? { discount_basis_points: Math.round(amount * 100) } : {}),
    ...(form.promotion_type === 'AMOUNT_OFF' ? { discount_amount_cents: Math.round(amount * 100) } : {}),
    ...(form.promotion_type === 'FIXED_PRICE' ? { fixed_price_cents: Math.round(amount * 100) } : {}),
    ...(form.promotion_type === 'BUY_X_GET_Y' ? { buy_quantity: Number(form.buy_quantity), get_quantity: Number(form.get_quantity), get_discount_basis_points: Math.round(Number(form.get_percent) * 100) } : {}),
    min_quantity: count(form.min_quantity),
    max_quantity_per_order: count(form.max_quantity_per_order),
    usage_cap_total: count(form.usage_cap_total),
    ticket_type_codes: codes(form.ticket_type_codes),
    price_zone_codes: codes(form.price_zone_codes),
    // Both ticked is "no restriction", which is also where a new channel would land.
    channels: channels.length === 2 ? [] : channels,
  };
}

function specialTerms(form: OfferFormState): Record<string, unknown> {
  return {
    name: form.item_name.trim(),
    description: form.item_description.trim(),
    price_cents: cents(form.price),
    max_per_order: Number(form.max_per_order || 10),
    stock_limit: count(form.stock_limit),
    channels: [...(form.web ? ['WEB'] : []), ...(form.box_office ? ['BOX_OFFICE'] : [])],
  };
}

/** What Add offer sends. */
export function createOfferBody(form: OfferFormState): Record<string, unknown> {
  const base = { offer_kind: form.offer_kind, label: form.label, detail: form.detail };
  switch (form.offer_kind) {
    case 'GIVEAWAY':
      return { ...base, giveaway_quantity: count(form.giveaway_quantity) };
    case 'CONCESSION_SPECIAL':
      return { ...base, special: specialTerms(form) };
    case 'TICKET_DEAL':
      return { ...base, deal: { ...dealTerms(form), status: form.activate ? 'ACTIVE' : 'DRAFT' } };
    default:
      return base;
  }
}

/**
 * What Save sends for an offer that exists: every field the form shows for
 * its kind. The kind is fixed. A used deal's type and scope are sent as they
 * were (the form locks them), which the API reads as no change.
 */
export function editOfferBody(form: OfferFormState): Record<string, unknown> {
  const base = { label: form.label, detail: form.detail };
  switch (form.offer_kind) {
    case 'GIVEAWAY':
      return { ...base, giveaway_quantity: count(form.giveaway_quantity) };
    case 'CONCESSION_SPECIAL':
      return { ...base, special: specialTerms(form) };
    case 'TICKET_DEAL':
      return { ...base, deal: dealTerms(form) };
    default:
      return base;
  }
}

/** A deal used on an order keeps its kind of discount (D-383); everything else still edits. */
export const dealKindLocked = (offer: Pick<PromotionOffer, 'offer_kind' | 'used_on_orders'>): boolean => offer.offer_kind === 'TICKET_DEAL' && (offer.used_on_orders ?? 0) > 0;
